import { z } from 'zod';

// ---------------------------------------------------------------------------
// Params & Queries
// ---------------------------------------------------------------------------

export const contactIdParam = z.object({
  id: z.string().uuid(),
});

export const listContactsQuery = z.object({
  hub_id: z.string().uuid(),
  search: z.string().optional(),
  tags: z.string().optional(), // comma-separated
  page: z.coerce.number().int().min(1).default(1),
  per_page: z.coerce.number().int().min(1).max(100).default(25),
});

export const createContactBody = z.object({
  hub_id: z.string().uuid(),
  contact_name: z.string().min(1),
  phone: z.string().optional(),
  email: z.string().email().optional(),
  instagram_sid: z.string().optional(),
  hub_user_id: z.string().uuid().optional(),
  tags: z.array(z.string()).default([]),
  metadata: z.record(z.unknown()).default({}),
  enabled: z.boolean().default(true),
}).refine(
  (data) => data.phone || data.email || data.instagram_sid,
  { message: 'At least one channel (phone, email, or instagram_sid) is required' }
);

export const updateContactBody = z.object({
  contact_name: z.string().min(1).optional(),
  phone: z.string().nullable().optional(),
  email: z.string().email().nullable().optional(),
  instagram_sid: z.string().nullable().optional(),
  hub_user_id: z.string().uuid().nullable().optional(),
  tags: z.array(z.string()).optional(),
  metadata: z.record(z.unknown()).optional(),
  enabled: z.boolean().optional(),
});

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type ContactIdParam = z.infer<typeof contactIdParam>;
export type ListContactsQuery = z.infer<typeof listContactsQuery>;
export type CreateContactBody = z.infer<typeof createContactBody>;
export type UpdateContactBody = z.infer<typeof updateContactBody>;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface OutboundContact {
  contact_id: string;
  hub_id: string;
  hub_user_id: string | null;
  contact_name: string;
  phone: string | null;
  email: string | null;
  instagram_sid: string | null;
  tags: string[];
  metadata: Record<string, unknown>;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface ListContactsResponse {
  contacts: OutboundContact[];
  total: number;
  page: number;
  per_page: number;
}

export interface CreateContactResponse {
  contact_id: string;
}
